import { toaster } from "@/utilities/toaster";
import { Guid } from "guid-typescript";
import EmployeeUserModule from "@/store/modules/employeeuser.module";
import { assign, Machine } from "xstate";
const employeeUserMachine = Machine({
    id: "employeeUserMachine",
    context: {
        searchData: {
            page: 1,
            pageSize: 10,
            sort: {
                field: "employeeId",
                type: "asc",
            },
            columnFilters: [],
        },
        employeeUserId: Guid.createEmpty(),
    },
    initial: "idle",
    states: {
        idle: {
            on: {
                GETTING_USERS: { target: "gettingUsers" },
            },
        },
        gettingUsers: {
            invoke: {
                id: "gettingUsers",
                src: (context, event) => {
                    return EmployeeUserModule.GetAllUsers(context.searchData);
                },
                onDone: { target: "loadedUsers" },
                onError: { target: "serviceUnavailable" },
            },
        },
        loadedUsers: {
            on: {
                ADD_USER: { target: "gettingEmployees" },
                EDIT_USER: { target: "editUserRole" },
                GETTING_USERS: { target: "gettingUsers", actions: "assignSearchData" },
                RESEND_INVITATION: { target: "rensendInvitation" },
                DEACTIVATE_USER: { target: "checkingHostileTakeover" },
                ACTIVATE_USER: { target: "activateUser" },
                HOSTILE_TAKE_OVER_USER: { target: "hostileTakeoverUser" },
            },
        },
        gettingEmployees: {
            invoke: {
                id: "gettingEmployees",
                src: (context, event) => {
                    return EmployeeUserModule.GetEmployeesEmailId();
                },
                onDone: { target: "loadedEmployeesForAddUser" },
                onError: { target: "serviceUnavailable" },
            },
        },
        loadedEmployeesForAddUser: {
            on: {
                ADDING_USER: { target: "markAsUser" },
                CANCEL_USER: { target: "loadedUsers" },
            },
        },
        rensendInvitation: {
            invoke: {
                id: "resendInvitation",
                src: (context, event) => {
                    return EmployeeUserModule.ResendInvitation(event.request);
                },
                onDone: { target: "loadedUsers", actions: "showSuccessMessage" },
                onError: { target: "serviceUnavailable" },
            },
        },
        checkingHostileTakeover: {
            invoke: {
                id: "checkingHostileTakeover",
                src: (context, event) => {
                    return EmployeeUserModule.CanDeactivateUser(event.userId);
                },
                onDone: [
                    {
                        target: "hostileTakeoverUser",
                        cond: (context, event) => event.data.isHostileTakeOver === true,
                    },
                    {
                        target: "deactivateUser",
                        cond: (context, event) => event.data.isHostileTakeOver === false,
                    },
                ],
                onError: { target: "loadedUsers" },
            },
        },
        deactivateUser: {
            tags: "showUserDeactivationUI",
            on: {
                DEACTIVATING_USER: { target: "deactivatingUser" },
                CANCEL_USER: { target: "loadedUsers" },
            },
        },
        deactivatingUser: {
            tags: "showUserDeactivationUI",
            invoke: {
                id: "deactivatingUser",
                src: (context, event) => {
                    return EmployeeUserModule.DeActivateUser(event.userId);
                },
                onDone: { target: "gettingUsers", actions: "showDeactivateMessage" },
                onError: { target: "loadedUsers" },
            },
        },
        activateUser: {
            tags: "showUserActivationUI",
            on: {
                ACTIVATING_USER: { target: "activatingUser" },
                CANCEL_USER: { target: "loadedUsers" },
            },
        },
        activatingUser: {
            tags: "showUserActivationUI",
            invoke: {
                id: "activatingUser",
                src: (context, event) => {
                    return EmployeeUserModule.ActivateUser(event.userId);
                },
                onDone: { target: "gettingUsers", actions: "showActivationMessage" },
                onError: { target: "loadedUsers" },
            },
        },
        editUserRole: {
            invoke: {
                id: "editUserRole",
                src: (context, event) => {
                    return EmployeeUserModule.EditEmployeeUserById(event.employeeUserId);
                },
                onDone: { target: "loadedEmployeesForEditUser" },
                onError: { target: "serviceUnavailable" },
            },
        },
        loadedEmployeesForEditUser: {
            on: {
                EDITING_USER: { target: "editingUserRole" },
                CANCEL_USER: { target: "loadedUsers" },
            },
        },
        markAsUser: {
            invoke: {
                id: "markAsUser",
                src: (context, event) => {
                    return EmployeeUserModule.AddEmployeeUser(event.payload);
                },
                onDone: { target: "gettingUsers", actions: "showSuccessMessage" },
                onError: { target: "serviceUnavailable" },
            },
        },
        editingUserRole: {
            invoke: {
                id: "editingUserRole",
                src: (context, event) => {
                    return EmployeeUserModule.UpdateEmployeeUser(event.payload);
                },
                onDone: { target: "gettingUsers", actions: "showSuccessMessage" },
                onError: { target: "serviceUnavailable" },
            },
        },
        hostileTakeoverUser: {
            tags: "showHostileConfirmationUI",
            on: {
                HOSTILE_TAKEN_OVER_USER: { target: "hostileTakenOverUserProjectDetails" },
                CANCEL_USER: { target: "loadedUsers" },
            },
        },
        hostileTakenOverUserProjectDetails: {
            tags: ["showHostileTakeoverUI", "showLoader"],
            invoke: {
                id: "hostileTakenOverUserProjectDetails",
                src: (context, event) => {
                    return EmployeeUserModule.HostileTakeoverUserProjectDetails(event.userId);
                },
                onDone: { target: "hostileTakeoverUserConfirmation" },
                onError: { target: "loadedUsers" },
            },
        },
        hostileTakeoverUserConfirmation: {
            on: {
                HOSTILE_TAKEOVER_CONFIRMATION: { target: "hostileTakeoverUserDeactivated" },
                CANCEL_USER: { target: "loadedUsers" },
            },
        },
        hostileTakeoverUserDeactivated: {
            tags: ["showLoader"],
            invoke: {
                id: "hostileTakeoverUserDeactivated",
                src: (context, event) => {
                    return EmployeeUserModule.saveHostileTakeoverUserProjectDetails(event.payload);
                },
                onDone: { target: "gettingUsers", actions: "showSuccessMessage" },
                onError: { target: "loadedUsers" },
            },
        },
        serviceUnavailable: {
            type: "final",
        },
    },
}, {
    actions: {
        showSuccessMessage: (context, event) => {
            toaster.ShowSuccess(event.data.successMessages[0]);
        },
        showDeactivateMessage: () => {
            toaster.ShowSuccess("The User has been deactivated.");
        },
        showActivationMessage: () => {
            toaster.ShowSuccess("The User has been activated.");
        },
        assignSearchData: assign({
            searchData: (context, event) => event.searchData,
        }),
        assignUserId: assign({
            employeeUserId: (context, event) => event.userId,
        }),
    },
    guards: {},
    activities: {},
    services: {},
});
export default employeeUserMachine;
